import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { UICard } from "../../../../../ui";
import { clx } from "../../../../../../utils/Helpers";
import { getEventRoleColor } from "../../../../../../utils/eventRoleColors";
import { formatPayrollAmount } from "../utils/formatPayrollValue";
import EventPayrollSectionEmptyState from "./EventPayrollSectionEmptyState";

const toEnglishOrdinal = (rank) => {
  const value = Number(rank);
  if (!Number.isFinite(value)) return String(rank ?? "");

  const remainder = value % 100;
  if (remainder >= 11 && remainder <= 13) return `${value}th`;

  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
};

const EventPayrollEarningsSection = ({
  title,
  meta,
  total,
  roles,
  labels,
  shiftLabels,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  className,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const hasRoles = roles.length > 0;

  return (
    <UICard
      type="info"
      className={clx(className, "event-payroll-generate__earnings-card", "event-payroll-generate__list-card")}
      title={title}
      subtitle={meta || undefined}
      action={
        <span className="event-payroll-generate__card-total text-label-lg">
          {formatPayrollAmount(total)}
        </span>
      }
    >
      {hasRoles ? (
        <ul className="event-payroll-generate__role-list">
          {roles.map((role) => (
            <li key={role.id} className="event-payroll-generate__role-item">
              <div className="event-payroll-generate__role-main">
                <span
                  className="event-payroll-generate__role-dot"
                  style={{ background: getEventRoleColor(role.name) }}
                  aria-hidden="true"
                />
                <div className="event-payroll-generate__role-copy">
                  <span className="event-payroll-generate__role-name text-label-md">
                    {role.name}
                  </span>
                  <span className="event-payroll-generate__role-rank text-body-xs">
                    {role.shiftType && shiftLabels[role.shiftType]
                      ? shiftLabels[role.shiftType]
                      : labels.roleRank.replace(
                          "{rank}",
                          lang === "ar" ? String(role.rank) : toEnglishOrdinal(role.rank),
                        )}
                  </span>
                </div>
              </div>
              <span className="event-payroll-generate__role-days text-label-md">
                {labels.days.replace("{count}", String(role.days))}
              </span>
              <span className="event-payroll-generate__role-rate text-label-md">
                {labels.ratePerDay.replace(
                  "{amount}",
                  formatPayrollAmount(role.ratePerDay),
                )}
              </span>
              <span className="event-payroll-generate__role-total text-label-md">
                {formatPayrollAmount(role.total)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <EventPayrollSectionEmptyState
          tone="muted"
          icon={emptyIcon}
          title={emptyTitle}
          description={emptyDescription}
        />
      )}
    </UICard>
  );
};

EventPayrollEarningsSection.propTypes = {
  title: PropTypes.string.isRequired,
  meta: PropTypes.string,
  total: PropTypes.number,
  roles: PropTypes.arrayOf(PropTypes.object),
  labels: PropTypes.shape({
    roleRank: PropTypes.string.isRequired,
    days: PropTypes.string.isRequired,
    ratePerDay: PropTypes.string.isRequired,
  }).isRequired,
  shiftLabels: PropTypes.object,
  emptyIcon: PropTypes.node.isRequired,
  emptyTitle: PropTypes.string,
  emptyDescription: PropTypes.string,
  className: PropTypes.string,
};

EventPayrollEarningsSection.defaultProps = {
  meta: "",
  total: 0,
  roles: [],
  shiftLabels: {},
  emptyTitle: "",
  emptyDescription: "",
  className: "ui-card--section",
};

export default EventPayrollEarningsSection;
